import React, { useState } from "react";
import axios from "axios";
import { showPlaceholderNames } from "../data/siteData";

function DemoSection() {
  const [formData, setFormData] = useState({
    fullName: "",
    clinicName: "",
    clinicType: "GP / Primary care",
    phone: "",
    email: "",
  });

  const [status, setStatus] = useState({ type: "", message: "", loading: false });
  const isFormLocked = status.type === "success";

  const baseURL = process.env.REACT_APP_MAIL_API_URL;
  const endpoint = process.env.REACT_APP_MAIL_API_ENDPOINT;
  const targetEmail = process.env.REACT_APP_CONTACT_US_EMAIL;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (status.type === "error") {
      setStatus({ type: "", message: "", loading: false });
    }
  };

  const handleClinicTypeChange = (e) => {
    setFormData((prev) => ({ ...prev, clinicType: e.target.value }));
    if (status.type === "error") {
      setStatus({ type: "", message: "", loading: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormLocked) return;

    if (!formData.fullName || !formData.email) {
      setStatus({
        type: "error",
        message: "Please fill in at least your full name and email address.",
        loading: false,
      });
      return;
    }

    if (!baseURL || !endpoint || !targetEmail || !process.env.REACT_APP_MAIL_API_KEY) {
      setStatus({
        type: "error",
        message: "Email service is not configured. Please try again later.",
        loading: false,
      });
      return;
    }

    const body = `
      <table border="0" cellpadding="8" cellspacing="0" style="width:100%; font-family: Arial, sans-serif; border-collapse:collapse;">
        <tr>
          <td style="font-weight:bold; width:160px;">Full name</td>
          <td>${formData.fullName}</td>
        </tr>
        <tr>
          <td style="font-weight:bold;">Clinic name</td>
          <td>${formData.clinicName || "-"}</td>
        </tr>
        <tr>
          <td style="font-weight:bold;">Clinic type</td>
          <td>${formData.clinicType}</td>
        </tr>
        <tr>
          <td style="font-weight:bold;">Phone number</td>
          <td>${formData.phone || "-"}</td>
        </tr>
        <tr>
          <td style="font-weight:bold;">Email address</td>
          <td>${formData.email}</td>
        </tr>
      </table>
    `;

    const payload = {
      service: "XSB_CARE_WEBSITE",
      type: "XSB_CARE_WEBSITE_DEMO_REQUEST",
      to: targetEmail,
      params: {
        subject: "BYOND CARE - Demo booking request",
        body,
      },
    };

    try {
      setStatus((prev) => ({ ...prev, loading: true, type: "", message: "" }));

      const response = await axios.post(`${baseURL}${endpoint}`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_MAIL_API_KEY}`,
        },
      });

      if (response.status === 200) {
        setStatus({
          type: "success",
          message:
            "Thank you – your demo request has been sent. We’ll reach out within 1 business day to confirm your slot.",
          loading: false,
        });
        setFormData({
          fullName: "",
          clinicName: "",
          clinicType: "GP / Primary care",
          phone: "",
          email: "",
        });
      } else {
        setStatus({
          type: "error",
          message: "We couldn’t send your request right now. Please try again in a moment.",
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error sending demo request:", error);
      setStatus({
        type: "error",
        message: "Something went wrong while sending your request. Please try again later.",
        loading: false,
      });
    }
  };

  return (
    <section id="demo" className="section demo">
      <div className="demo__grid">
        <div className="demo__copy">
          <p className="eyebrow">Book a demo</p>
          <h2>See byond care in action.</h2>
          <p>
            Book a personalised 30-minute demo with our team. We&apos;ll walk you
            through the platform, answer your questions, and show you exactly how
            byond care works for clinics like yours.
          </p>
          <ul className="demo__points">
            <li>
              A live walkthrough of the full platform queue, EMR, billing,
              pharmacy, patient app
            </li>
            <li>Answers to your specific questions about your clinic setup</li>
            <li>Honest pricing conversation no hard sell</li>
          </ul>
          <blockquote className="demo__quote">
            <p>
              &quot;The demo took 30 minutes and answered every question I had. We were
              onboarded within a week.&quot;
            </p>
            {showPlaceholderNames && (
              <cite>Dr. [Name] replace with real testimonial before launch</cite>
            )}
          </blockquote>
        </div>
        <div className="demo__form-wrap">
          <form className="demo__form" onSubmit={handleSubmit}>
            <label>
              Full name
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Dr. Ahmad bin Ali"
                disabled={isFormLocked}
              />
            </label>
            <label>
              Clinic name
              <input
                type="text"
                name="clinicName"
                value={formData.clinicName}
                onChange={handleChange}
                placeholder="Klinik Sejahtera"
                disabled={isFormLocked}
              />
            </label>
            <label>
              Clinic type
              <select
                name="clinicType"
                value={formData.clinicType}
                onChange={handleClinicTypeChange}
                disabled={isFormLocked}
              >
                <option value="GP / Primary care">GP / Primary care</option>
                <option value="Specialist clinic">Specialist clinic</option>
                <option value="Dental clinic">Dental clinic</option>
                <option value="Aesthetic clinic">Aesthetic clinic</option>
              </select>
            </label>
            <label>
              Phone number
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+60 12-345 6789"
                disabled={isFormLocked}
              />
            </label>
            <label>
              Email address
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="doctor@klinik.com.my"
                required
                disabled={isFormLocked}
              />
            </label>
            <button type="submit" className="btn" disabled={status.loading || isFormLocked}>
              {status.loading ? "Sending..." : "Book my demo"}
            </button>
            <p className="demo__note">
              No obligations. We&apos;ll confirm by WhatsApp within 24 hours.
            </p>
            {status.message && (
              <p
                className={`demo__status demo__status--${status.type}`}
                aria-live="polite"
              >
                {status.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default DemoSection;
